import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import '../AdminPrivacyPolicyPage/AdminPrivacyPolicyPage.css';

const API_BASE_URL = '/api';

const AdminTermsConditions2Page = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageData, setPageData] = useState({
    title: 'Terms & Conditions',
    description: 'Learn how we collect, use, and protect your personal information with full transparency.'
  });
  const [termsContent, setTermsContent] = useState({
    paragraph1: '',
    paragraph2: '',
    paragraph3: '',
    paragraph4: ''
  });

  useEffect(() => {
    fetchTermsContent();
  }, []);

  const fetchTermsContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/settings/content/terms-conditions`);
      if (response.ok) {
        const data = await response.json();
        setPageData({
          title: data.title || 'Terms & Conditions',
          description: data.description || 'Learn how we collect, use, and protect your personal information with full transparency.'
        });
        
        let paragraphs = [];
        try {
          paragraphs = JSON.parse(data.content || '[]');
        } catch (e) {
          paragraphs = [data.content || ''];
        }
        
        setTermsContent({
          paragraph1: paragraphs[0] || '',
          paragraph2: paragraphs[1] || '',
          paragraph3: paragraphs[2] || '',
          paragraph4: paragraphs[3] || ''
        });
      }
    } catch (error) {
      console.error('Error fetching terms content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const contentArray = [
        termsContent.paragraph1,
        termsContent.paragraph2,
        termsContent.paragraph3,
        termsContent.paragraph4
      ].filter(p => p.trim() !== '');

      const response = await fetch(`${API_BASE_URL}/settings/content/terms-conditions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: pageData.title,
          description: pageData.description,
          content: JSON.stringify(contentArray)
        })
      });

      if (response.ok) {
        setIsEditing(false);
      } else {
        const error = await response.json();
        alert('Error saving: ' + (error.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving terms content:', error);
      alert('Error saving content');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-privacy-content">
        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : (
          <>
            <div className="privacy-page-header">
              <div className="privacy-title-section">
                <h1 className="privacy-page-title">{pageData.title}</h1>
                <p className="privacy-page-description">{pageData.description}</p>
              </div>
              <button 
                className={`edit-privacy-btn ${isEditing ? 'save' : ''}`}
                onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                disabled={saving}
              >
                {isEditing ? (
                  <>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{saving ? 'Saving...' : 'Save'}</span>
                  </>
                ) : (
                  <>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 20H21" stroke="#6D6D6D" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16.5 3.50023C16.8978 3.1024 17.4374 2.87891 18 2.87891C18.2786 2.87891 18.5544 2.93378 18.8118 3.04038C19.0692 3.14699 19.303 3.30324 19.5 3.50023C19.697 3.69721 19.8532 3.93106 19.9598 4.18843C20.0665 4.4458 20.1213 4.72165 20.1213 5.00023C20.1213 5.2788 20.0665 5.55465 19.9598 5.81202C19.8532 6.06939 19.697 6.30324 19.5 6.50023L7 19.0002L3 20.0002L4 16.0002L16.5 3.50023Z" stroke="#6D6D6D" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Edit</span>
                  </>
                )}
              </button>
            </div>

            <div className="privacy-content-section">
              {isEditing ? (
                <div className="privacy-edit-form">
                  <div className="privacy-form-group">
                    <label>Paragraph 1</label>
                    <textarea 
                      value={termsContent.paragraph1}
                      onChange={(e) => setTermsContent({...termsContent, paragraph1: e.target.value})}
                      rows={4}
                    />
                  </div>
                  <div className="privacy-form-group">
                    <label>Paragraph 2</label>
                    <textarea 
                      value={termsContent.paragraph2}
                      onChange={(e) => setTermsContent({...termsContent, paragraph2: e.target.value})}
                      rows={4}
                    />
                  </div>
                  <div className="privacy-form-group">
                    <label>Paragraph 3</label>
                    <textarea 
                      value={termsContent.paragraph3}
                      onChange={(e) => setTermsContent({...termsContent, paragraph3: e.target.value})}
                      rows={4}
                    />
                  </div>
                  <div className="privacy-form-group">
                    <label>Paragraph 4</label>
                    <textarea 
                      value={termsContent.paragraph4}
                      onChange={(e) => setTermsContent({...termsContent, paragraph4: e.target.value})}
                      rows={2}
                    />
                  </div>
                </div>
              ) : (
                <div className="privacy-paragraphs">
                  {termsContent.paragraph1 && <p>{termsContent.paragraph1}</p>}
                  {termsContent.paragraph2 && <p>{termsContent.paragraph2}</p>}
                  {termsContent.paragraph3 && <p>{termsContent.paragraph3}</p>}
                  {termsContent.paragraph4 && <p>{termsContent.paragraph4}</p>}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTermsConditions2Page;
